import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {ChangelogSource} from 'config-migration/import/import-changelog/ImportChangelog';
import {HighImpactChange} from 'config-migration/MigrationConstants';
import {HighImpactChangeIcon} from 'config-migration/shared-ui-components/HighImpactChangeIcon';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GREEN, ORANGE, RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {getMaxLogLevel, LogLevel, LogLevelMapping} from './LogLevel';

export interface LogMessageData {
  details: Array<LogDetail>;
  message: string | JSX.Element;
  changelogSource?: ChangelogSource;
}

export interface LogDetail {
  level: LogLevel;
  highImpactChange?: HighImpactChange;
  message: string | JSX.Element;
}

export function makeSimpleLogMessage(message: string | JSX.Element): LogMessageData {
  return {message, details: []};
}

/**
 * Formats the given logs into a human-readable format suitable for writing to a log file.
 */
export function formatLogs(logs: Array<LogMessageData>): string {
  const messages: Array<string> = logs.map((log) => {
    const maxLevel: LogLevel | HighImpactChange = getMaxLogLevel(log.details.map((detail) => detail.level));
    let lines: Array<string> = [`${maxLevel}: ${log.message}`];
    lines = lines.concat(log.details.map((detail) => `${detail.level}: ${detail.message}`));

    // Indent detail logs under the main log line.
    return lines.join('\n  ');
  });

  return messages.join('\n');
}

export interface LogIconProps {
  logLevel: LogLevel;
  highImpactChange?: HighImpactChange;
  className: string;
  /** Whether to render the 'info' icon. If false, renders a blank icon. */
  renderInfo: boolean;
}

export function LogIcon(props: LogIconProps): JSX.Element {
  if (props.highImpactChange) {
    return <HighImpactChangeIcon highImpactChange={props.highImpactChange} />;
  } else if (props.logLevel === LogLevel.ERROR) {
    return <BenchlingIcon icon="error" className={classNames(props.className, css(styles.errorIcon))} />;
  } else if (props.logLevel === LogLevel.WARNING) {
    return <BenchlingIcon icon="warning" className={classNames(props.className, css(styles.warningIcon))} />;
  } else if (props.renderInfo) {
    return <BenchlingIcon icon="check" className={classNames(props.className, css(styles.infoIcon))} />;
  } else {
    return (
      <BenchlingIcon icon="notdef" className={classNames(css(styles.blankIcon), css(styles.detailIcon))} />
    );
  }
}

export function LogMessage(props: LogMessageData): JSX.Element {
  const details = _.sortBy(props.details, (detail) => -1 * LogLevelMapping[detail.level]);
  const maxLogLevel = getMaxLogLevel(details.map((detail) => detail.level));
  const containsHighImpactChange = details.some((detail) => detail.highImpactChange);

  // If there are high impact changes and no logs >= WARN, show the WARN symbol for the message.
  let logLevelForMessage = getMaxLogLevel([
    maxLogLevel,
    containsHighImpactChange ? LogLevel.WARNING : LogLevel.INFO,
  ]);

  // If the changelog source is SUMMARY and the log level is WARNING, reduce it to INFO
  if (props.changelogSource === ChangelogSource.SUMMARY && logLevelForMessage === LogLevel.WARNING) {
    logLevelForMessage = LogLevel.INFO;
  }

  return (
    <div className={css(styles.logRow)} {...getTestProps({component: 'LogMessage', element: 'root'})}>
      <div className="u-flex-display">
        <LogIcon
          className={classNames(css(styles.objectIcon))}
          logLevel={logLevelForMessage}
          renderInfo={true}
        />
        {props.message}
      </div>
      {details.map(({message, level, highImpactChange}, i) => (
        <div
          className={classNames(css(styles.listItem))}
          key={i}
          {...getTestProps({component: 'LogMessage', element: 'detail', index: `${i}`})}
          data-amp-safe={false}
        >
          <LogIcon
            logLevel={level}
            highImpactChange={highImpactChange}
            className={classNames(css(styles.detailIcon))}
            renderInfo={false}
          />
          {message}
        </div>
      ))}
    </div>
  );
}

/**
 * Helper to render a 'simple' log component that simply shows a section-level message without any
 * object-level details.
 */
export function SimpleLog(props: {message: string | JSX.Element}): JSX.Element {
  return <Log logMessages={[makeSimpleLogMessage(props.message)]} />;
}

/**
 * Component that renders a group of LogMessages corresponding to a cohesive section. Examples could include
 * rendering the import log for an entire section (entity schemas), or rendering the log for a template
 * collection and its templates.
 */
export default function Log({logMessages}: {logMessages: Array<LogMessageData>}): JSX.Element {
  return (
    <div>
      {logMessages.map((logMessage, i) => (
        <LogMessage key={i} {...logMessage} />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  errorIcon: {
    color: RED,
  },
  warningIcon: {
    color: ORANGE,
  },
  infoIcon: {
    color: GREEN,
  },
  logRow: {
    marginBottom: 8,
    display: 'flex',
    flexDirection: 'column',
  },
  listItem: {
    marginLeft: 16,
    marginTop: 6,
    display: 'flex',
  },
  objectIcon: {width: 14, height: 14, flex: 'none', marginRight: 6},
  detailIcon: {
    height: 12,
    width: 12,
    flex: 'none',
    marginRight: 6,
    marginTop: 2,
  },
  blankIcon: {
    color: 'transparent',
  },
});
